/**
 * Troca comprimento↔largura de uma peça importada via STEP (formato de
 * /api/step/parse) -- ciclo 11, docs/referencia/parser-step.md §3.
 *
 * NÃO é rotação física da peça: `_get_dimensions_and_axes` (backend) apenas
 * RÓTULA qual eixo XYZ do STEP chama-se "comprimento"/"largura" (o maior
 * dos dois, por convenção), sem girar o sólido. Essa convenção pode
 * divergir da que a manufatura/cadastro usa (achado real, ciclo 11: peça
 * 510524003/DIVISAO tem comprimento=303/largura=269 pela heurística do
 * STEP, mas o .mpr nativo dessa peça tem _BSX=269/_BSY=303 -- o eixo
 * "comprimento" de verdade é o MENOR dos dois, e nada nos números de saída
 * denuncia isso). Por isso a confirmação de orientação é sempre mostrada
 * ao usuário no import (nenhum sinal geométrico decide isso com certeza).
 *
 * Trocar os eixos aqui é, portanto, um relabeling puro: transpor x/y de
 * cada furo/rasgo e remapear o `lado`/`face` (BM) trocando o eixo (X↔Y)
 * mas mantendo o sentido de entrada (P/M) -- diferente de uma rotação de
 * 90°, que troca eixo E sentido (ver furoTransforms.js, PROJ[90]). Não
 * reusa o pipeline de rotação 90°/270° de propósito: aquela área tem gap
 * conhecido e não validado (campo-bm-horizontal.md §9.4).
 */

// Mesma regra oficial [CNC] de furoTransforms.js/geometria_horizontal.py,
// mas aqui usada só pra TRANSPOR eixo (X<->Y mantendo P/M), não para
// espelhar em torno de uma borda -- por isso a tabela é local e não a
// mesma PROJ[90] (que combina troca de eixo com espelhamento espacial).
const TROCA_EIXO_LADO = { XP: 'YP', XM: 'YM', YP: 'XP', YM: 'XM' };
const TROCA_EIXO_DIRECAO = { X: 'Y', Y: 'X' };

/**
 * @param {object} peca - peça no formato de /api/step/parse (comprimento,
 *   largura, espessura, furos[], rasgos[]).
 * @returns {object} nova peça (não modifica `peca`) com comprimento/largura
 *   trocados e furos/rasgos recalculados no novo referencial.
 */
export function trocarEixosComprimentoLargura(peca) {
  const comprimento = peca.largura;
  const largura = peca.comprimento;

  const furos = (peca.furos || []).map((furo) => {
    const novo = { ...furo, x: furo.y, y: furo.x };
    if (furo.tipo === 'horizontal') {
      novo.lado = TROCA_EIXO_LADO[furo.lado] || furo.lado;
    }
    return novo;
  });

  const rasgos = (peca.rasgos || []).map((rasgo) => ({
    ...rasgo,
    x: rasgo.y,
    y: rasgo.x,
    direcao: TROCA_EIXO_DIRECAO[rasgo.direcao] || rasgo.direcao,
    face: TROCA_EIXO_LADO[rasgo.face] || rasgo.face,
  }));

  return { ...peca, comprimento, largura, furos, rasgos };
}
